import { Component, Inject, Injector, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from "@angular/material/dialog";
import { GenericComponent } from "@shared/components/generic.component";
import { UploadErrorsTemplateModalComponent } from "../upload-errors-template-modal/upload-errors-template-modal.component";

export interface GenericUploadDialogData {
  title: string; 
  subtitle: string; 
  contextParams?: Record<string, any>; 
  downloadTemplateFn: () => Promise<Blob>; 
  uploadFn: (file: File) => Promise<{ status: string; errors?: any[]; message?: string }>;
  templateFileName?: string;
  
  errorsTitle?: string;
  errorsSubtitle?: string;
  errorColumns?: { key: string; label: string }[];
}

@Component({
  selector: 'app-driveb-upload-template-modal',
  templateUrl: './upload-template-modal.component.html',
  styleUrl: './upload-template-modal.component.scss',
  standalone: false
})
export class UploadTemplateModalComponent extends GenericComponent implements OnInit {
  selectedFile: File | null = null;
  loading = false;

  constructor(
    injector: Injector,
    private dialogRef: MatDialogRef<UploadTemplateModalComponent>,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: GenericUploadDialogData
  ) {
    super(injector);
  }

  ngOnInit(): void {}

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.selectedFile = input.files[0];
    }
  }

  async downloadTemplate(): Promise<void> {
    if (!this.data.downloadTemplateFn) return;
    this.loading = true;
    try {
      const blob = await this.data.downloadTemplateFn();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = this.data.templateFileName || "template.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      // erro de download, pode exibir notificação aqui
    } finally {
      this.loading = false;
    }
  }

  async upload(): Promise<void> {
    if (!this.selectedFile || !this.data.uploadFn) return;
    this.loading = true;
    try {
      const res = await this.data.uploadFn(this.selectedFile);
      const hasErrors = Array.isArray(res.errors) && res.errors.length > 0;
      const isErrorStatus = res.status === "ERROR" || res.status === "BAD_REQUEST";

      if (hasErrors || isErrorStatus) {
        const formattedErrors = hasErrors
          ? res.errors.map((error: any) => ({
              line: error.line,
              description: error.message,
            }))
          : [{ line: 0, description: res.message || "Unknown error during upload." }];

        this.openErrorsModal(formattedErrors);
      } else {
        this.dialogRef.close({ success: true });
      }
    } catch (err: any) {
      const errorMessage = err?.error?.message || err?.message || "Unknown server error.";
      this.openErrorsModal([{ line: 0, description: errorMessage }]);
    } finally {
      this.loading = false;
    }
  }

  private openErrorsModal(errors: any[]) {
    this.dialog.open(UploadErrorsTemplateModalComponent, {
      width: "700px",
      data: {
        title: this.data.errorsTitle,
        subtitle: this.data.errorsSubtitle || "upload-template-modal.subtitle",
        columns: this.data.errorColumns || [
          { key: "line", label: "upload-template-modal.label-line" },
          { key: "description", label: "upload-template-modal.label-description" },
        ],
        errors,
      },
    });
  }

  cancel() {
    this.dialogRef.close(false);
  }
}
